import { Fragment, useEffect, useMemo, useRef, useState } from "react"
import { useFormContext, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { GenderFemale, GenderMale } from "@phosphor-icons/react"
import { t } from "i18next"
import { useUser } from "@/apis/bff"
import { ScrollArea } from "@/components/ui/scroll-area"
import { SearchContent, SearchItem, SearchSelectInput, SearchSeparator, SuggestionSearch } from "@/components/ui/search"
import { useAgentOptions } from "@/hooks/useAgentOptions"
import { useClickOutside } from "@/hooks/useClickOutside"
import { AgentOrGenderSkillOption, GenderSkillOption, handleFilterOptions } from "@/lib/filter-agent-options"
import { cn } from "@/lib/utils"
import { TeleconsultationMode } from "@/modules/patients/agenda/teleconsultation/booking/teleconsultation-drawer"
import { TeleconsultationForm } from "@/modules/patients/agenda/teleconsultation/booking/teleconsultation-schema"
import { SearchOption } from "@/modules/patients/components/search-option"

const genderOptions: GenderSkillOption[] = [
  {
    kind: "Gender",
    label: t("patient.agenda.agentSkills.onlyMale"),
    icon: <GenderMale />,
    value: "MALE",
  },
  {
    kind: "Gender",
    label: t("patient.agenda.agentSkills.onlyFemale"),
    icon: <GenderFemale />,
    value: "FEMALE",
    last: true,
  },
]

const AgentSkill = (props: { mode: TeleconsultationMode; favoriteAgents?: readonly string[] }) => {
  const { mode, favoriteAgents } = props
  const { t } = useTranslation()

  const agentOptions = useAgentOptions().filter((option) => !option.isDeleted)
  const currentUser = useUser()
  const searchRef = useRef<HTMLInputElement>(null)
  const searchResultRef = useRef<HTMLDivElement>(null)

  const { control, setValue, getValues } = useFormContext<TeleconsultationForm>()
  const genderWatch = useWatch({ control, name: "gender" })
  const agentUserNameWatch = useWatch({ control, name: "agentUserName" })

  const [searchResultVisible, setSearchResultVisible] = useState(false)
  const [isReadyToEnterText, setIsReadyToEnterText] = useState(true)
  const [searchText, setSearchText] = useState(() => {
    const gender = getValues("gender")
    const agentUserName = getValues("agentUserName")

    let initialOption: AgentOrGenderSkillOption | undefined
    if (mode.kind === "EDITING_APPOINTMENT") {
      initialOption = agentOptions.find((option) => option.value === (gender === "UNKNOWN" ? agentUserName : gender))
    } else if (mode.kind === "FOLLOWUP_FOR_PROGRESS_NOTE") {
      initialOption = agentOptions.find((option) => option.value === currentUser.userName)
    } else if (gender || agentUserName) {
      initialOption = agentOptions.find((option) => option.value === (gender ?? agentUserName))
    }

    if (initialOption?.label) {
      return initialOption.label
    } else {
      setIsReadyToEnterText(false)
      return ""
    }
  })

  const filteredOptions = useMemo(
    () => handleFilterOptions(genderOptions, agentOptions, searchText, favoriteAgents),
    [agentOptions, searchText, favoriteAgents],
  )

  useEffect(() => {
    if (isReadyToEnterText) return
    const option = filteredOptions.find((option) => option.value === (genderWatch ?? agentUserNameWatch))
    setSearchText(option ? option.label : "")
  }, [genderWatch, agentUserNameWatch, filteredOptions, isReadyToEnterText])

  const handleSelectOption = (option: AgentOrGenderSkillOption) => {
    if (option.kind === "Gender") {
      setValue("gender", option.value, { shouldDirty: true })
      setValue("agentUserName", null, { shouldDirty: true })
    } else {
      setValue("agentUserName", option.value, { shouldDirty: true })
      setValue("gender", null, { shouldDirty: true })
    }

    setIsReadyToEnterText(false)
    setSearchResultVisible(false)
  }

  const handleClear = () => {
    setSearchText("")
    setValue("agentUserName", null, { shouldDirty: true })
    setValue("gender", null, { shouldDirty: true })
  }

  useClickOutside((event: MouseEvent) => {
    if (
      searchResultVisible &&
      searchResultRef.current &&
      event.target instanceof HTMLElement &&
      !searchResultRef.current.contains(event.target)
    ) {
      setSearchResultVisible(false)
    }
  }, searchRef)

  return (
    <SuggestionSearch>
      <SearchSelectInput
        ref={searchRef}
        label={t("patient.agenda.agentSkills.agent")}
        value={searchText}
        onChange={({ target: { value } }) => {
          setIsReadyToEnterText(true)
          if (value.length === 0) {
            handleClear()
            return
          }
          setSearchText(value)
        }}
        onClear={handleClear}
        onFocus={() => setSearchResultVisible(true)}
        className="z-30"
        testId="agent"
      />
      <SearchContent ref={searchResultRef} className={cn("px-0", searchResultVisible ? "show" : "hidden")}>
        <ScrollArea>
          <div className="py-2" data-testid="agent-skills-search-section">
            {filteredOptions.length === 0 ? (
              <SearchItem
                disabled
                className="text-on-surface z-30 mx-2 cursor-default overflow-hidden rounded-md py-1.5"
                onSelect={() => setSearchResultVisible(false)}>
                {t("common.nothingFound")}
              </SearchItem>
            ) : (
              filteredOptions.map((option, idx) => {
                const { value, label, icon, heading, last } = option
                const hasNext = filteredOptions[idx + 1]
                return (
                  <Fragment key={`${value}-${heading}`}>
                    {heading && (
                      <div className="text-on-surface-subdued py-2.5 pl-4 text-sm leading-5 font-medium">
                        {t(heading)}
                      </div>
                    )}
                    <SearchOption
                      searchValue={searchText}
                      value={value}
                      title={label}
                      onSelect={() => handleSelectOption(option)}
                      icon={icon}
                      description={option.kind === "User" && option.specialties ? option.specialties : undefined}
                      className="mx-2"
                    />
                    {last && hasNext && <SearchSeparator className="border-t-border mt-1 border-t" />}
                  </Fragment>
                )
              })
            )}
          </div>
        </ScrollArea>
      </SearchContent>
    </SuggestionSearch>
  )
}

export { AgentSkill }
